/**
 * Marketing > SEO Dashboard — Painel de SEO técnico do site VUKZ
 * Tabs: Visão Geral | Checklist | Páginas | Structured Data | Analytics | Performance
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Globe, FileText,
  Search, Code, Gauge, BarChart3, Database, Zap, Activity
} from 'lucide-react';
import R from '../../../navigation/opsRoutes';
import { useGovernanceLang } from '../../../contexts/GovernanceLangContext';

const SEO_LABELS = {
  pt: {
    pageTitle: 'SEO do Site',
    pageSubtitle: 'Diagnóstico técnico, structured data, analytics e performance.',
    cardOk: 'OK',
    cardWarn: 'Atenção',
    cardPending: 'Pendente',
    cardChecklist: 'Checklist',
    badgeWarn: 'Atenção',
    badgePending: 'Pendente',
    tabs: [
      { id: 'overview', label: 'Visão Geral' },
      { id: 'checklist', label: 'Checklist' },
      { id: 'pages', label: 'Páginas' },
      { id: 'structured', label: 'Structured Data' },
      { id: 'analytics', label: 'Analytics' },
      { id: 'performance', label: 'Performance' },
    ],
    checklistTitle: 'Checklist SEO Técnico',
    checklistDone: (done, total) => `${done} de ${total} concluídos`,
    pagesTitle: 'Páginas Estratégicas',
    thPage: 'Página', thType: 'Tipo', thUrl: 'URL', thKeyword: 'Palavra-chave', thSections: 'Seções Planejadas', thStatus: 'Status',
    structuredTitle: 'Dados Estruturados (JSON-LD)',
    thSchema: 'Schema', thLocation: 'Localização', thFile: 'Arquivo', thNotes: 'Notas',
    analyticsTitle: 'Analytics & Conversões (GA4)',
    thItem: 'Item',
    analyticsNote: (code) => <>Para ativar: Adicione <code className="bg-gray-200 px-1 rounded">{code}</code> no arquivo <code className="bg-gray-200 px-1 rounded">.env</code> e faça redeploy.</>,
    performanceTitle: 'Core Web Vitals & Performance',
    thMetric: 'Métrica', thTarget: 'Meta',
    perfFormulas: 'Fórmulas de referência:',
  },
  en: {
    pageTitle: 'Site SEO',
    pageSubtitle: 'Technical diagnostics, structured data, analytics and performance.',
    cardOk: 'OK',
    cardWarn: 'Warning',
    cardPending: 'Pending',
    cardChecklist: 'Checklist',
    badgeWarn: 'Warning',
    badgePending: 'Pending',
    tabs: [
      { id: 'overview', label: 'Overview' },
      { id: 'checklist', label: 'Checklist' },
      { id: 'pages', label: 'Pages' },
      { id: 'structured', label: 'Structured Data' },
      { id: 'analytics', label: 'Analytics' },
      { id: 'performance', label: 'Performance' },
    ],
    checklistTitle: 'Technical SEO Checklist',
    checklistDone: (done, total) => `${done} of ${total} completed`,
    pagesTitle: 'Strategic Pages',
    thPage: 'Page', thType: 'Type', thUrl: 'URL', thKeyword: 'Keyword', thSections: 'Planned Sections', thStatus: 'Status',
    structuredTitle: 'Structured Data (JSON-LD)',
    thSchema: 'Schema', thLocation: 'Location', thFile: 'File', thNotes: 'Notes',
    analyticsTitle: 'Analytics & Conversions (GA4)',
    thItem: 'Item',
    analyticsNote: (code) => <>To activate: Add <code className="bg-gray-200 px-1 rounded">{code}</code> to your <code className="bg-gray-200 px-1 rounded">.env</code> file and redeploy.</>,
    performanceTitle: 'Core Web Vitals & Performance',
    thMetric: 'Metric', thTarget: 'Target',
    perfFormulas: 'Reference formulas:',
  },
};

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1: VISÃO GERAL
// ═══════════════════════════════════════════════════════════════════════════════
const SEO_STATUS = [
  { label: 'Title Tag', status: 'ok', detail: 'Dinâmico via react-helmet-async (template: "Página | VUKZ")' },
  { label: 'Meta Description', status: 'ok', detail: 'Dinâmica por página pública' },
  { label: 'Open Graph', status: 'ok', detail: 'og:title, og:description, og:image configurados' },
  { label: 'Twitter Card', status: 'ok', detail: 'summary_large_image configurado' },
  { label: 'Canonical URL', status: 'ok', detail: 'Implementado via SEOHead component' },
  { label: 'robots.txt', status: 'ok', detail: 'Bloqueia /governance/, /feed, /chat e áreas internas' },
  { label: 'Sitemap.xml', status: 'ok', detail: 'Criado com páginas públicas (/, /estudos)' },
  { label: 'Structured Data', status: 'ok', detail: 'Organization (home) + Article (estudos) implementados' },
  { label: 'Páginas Públicas', status: 'ok', detail: '/estudos e /estudos/:slug com meta tags dinâmicas' },
  { label: 'noindex App Interno', status: 'ok', detail: 'GovernanceLayout adiciona noindex automaticamente' },
  { label: 'Core Web Vitals', status: 'ok', detail: 'web-vitals coletando CLS, LCP, FID → GA4' },
  { label: 'Analytics (GA4)', status: 'warning', detail: 'Código pronto — aguardando REACT_APP_GA_MEASUREMENT_ID no .env' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2: CHECKLIST
// ═══════════════════════════════════════════════════════════════════════════════
const CHECKLIST = [
  { item: 'Instalar react-helmet-async para meta tags dinâmicas', done: true },
  { item: 'Definir title padrão + template por página', done: true },
  { item: 'Definir meta description por página pública', done: true },
  { item: 'Adicionar Open Graph tags (og:title, og:description, og:image)', done: true },
  { item: 'Adicionar Twitter Card tags', done: true },
  { item: 'Criar/atualizar sitemap.xml com páginas públicas', done: true },
  { item: 'Atualizar robots.txt com Disallow para /governance/, /feed, /chat', done: true },
  { item: 'Implementar canonical URL em todas as páginas públicas', done: true },
  { item: 'Adicionar noindex meta para área logada do app', done: true },
  { item: 'Verificar 404 handling e redirects', done: true },
  { item: 'Criar structured data Organization (JSON-LD)', done: true },
  { item: 'Criar structured data para /estudos (Article schema)', done: true },
  { item: 'Integrar Google Analytics / GTM (GA4)', done: true },
  { item: 'Configurar eventos de conversão (signup, trial)', done: true },
  { item: 'Monitorar Core Web Vitals (web-vitals lib)', done: true },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3: PÁGINAS ESTRATÉGICAS
// ═══════════════════════════════════════════════════════════════════════════════
const STRATEGIC_PAGES = [
  { page: 'VUKZ Home', type: 'Institucional', url: '/', keyword: 'inteligência esportiva', status: 'Ativo', sections: 'Performance OS, Rankings, Mind, Swim Vision, Azure AI/Microsoft badges, CTA cadastro' },
  { page: 'Swim Vision', type: 'Produto', url: '/swim-vision', keyword: 'análise de natação por IA', status: 'Planejar', sections: 'Visão computacional multi-câmera, reconstrução 3D, estimativa de postura, sem sensores' },
  { page: 'TrackVision', type: 'Produto', url: '/trackvision', keyword: 'biomecânica de corrida por vídeo', status: 'Planejar', sections: 'Laudos biomecânicos, tempo de voo, contato com solo, cadência, oscilação vertical' },
  { page: 'IA para Clubes', type: 'Landing Page', url: '/clubes', keyword: 'software de performance esportiva', status: 'Planejar', sections: 'RBAC (treinadores, gestores, médicos, scouting), demo permissões' },
  { page: 'IA para Natação', type: 'Landing Page', url: '/natacao-ia', keyword: 'tecnologia para natação', status: 'Planejar', sections: 'Automação treino aquático, cargas diárias, metas baseadas em dados históricos' },
  { page: 'IA para Atletismo', type: 'Landing Page', url: '/atletismo', keyword: 'análise biomecânica atletismo', status: 'Planejar', sections: 'Detecção de assimetrias, prevenção de lesões, laudos de corrida e saltos' },
  { page: 'Reakt', type: 'Produto', url: '/reakt', keyword: 'sensores esportivos inteligentes', status: 'Planejar', sections: 'Integração com vestíveis, intensidade de treino, dados de sensores corporais' },
  { page: 'Estudos', type: 'Conteúdo', url: '/estudos', keyword: 'estatísticas natação brasil', status: 'Ativo', sections: 'Artigos com dados reais, competições oficiais, SEOHead + ArticleSchema ativos' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 4: STRUCTURED DATA
// ═══════════════════════════════════════════════════════════════════════════════
const STRUCTURED_DATA_ITEMS = [
  { schema: 'Organization', location: 'Homepage (/)', file: 'src/components/seo/StructuredData.jsx', status: 'Ativo', notes: 'Nome, logo, sameAs (LinkedIn, GitHub), sponsor Microsoft, contactPoint' },
  { schema: 'Article', location: '/estudos/:slug', file: 'src/components/seo/StructuredData.jsx', status: 'Ativo', notes: 'headline, author, publisher, datePublished/Modified dinâmicos do artigo' },
  { schema: 'SoftwareApplication', location: '/swim-vision, /trackvision, /reakt', file: 'src/components/seo/StructuredData.jsx', status: 'Pronto (aguarda página)', notes: 'Componente criado — usar quando landing pages forem publicadas' },
  { schema: 'FAQPage', location: 'Landing pages', file: 'src/components/seo/StructuredData.jsx', status: 'Pronto (aguarda página)', notes: 'Para rodapé de páginas estratégicas — melhora AI snippets' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 5: ANALYTICS & CONVERSÕES
// ═══════════════════════════════════════════════════════════════════════════════
const ANALYTICS_ITEMS = [
  { item: 'GA4 Script Loader', file: 'src/utils/analytics.js', status: 'Pronto', notes: 'initGA() — carrega gtag.js com send_page_view: false' },
  { item: 'Page View Tracker', file: 'src/components/seo/AnalyticsTracker.jsx', status: 'Ativo', notes: 'Escuta React Router, delay 120ms para Helmet atualizar title' },
  { item: 'trackSignUp(plan)', file: 'src/utils/analytics.js', status: 'Pronto', notes: 'Disparar no RegisterPage após sucesso do backend' },
  { item: 'trackTrialStart(tool, days)', file: 'src/utils/analytics.js', status: 'Pronto', notes: 'Disparar ao ativar período de teste de ferramenta' },
  { item: 'trackFeatureUse(name)', file: 'src/utils/analytics.js', status: 'Pronto', notes: 'Engagement: uso de features específicas (Swim Vision, rankings)' },
  { item: 'GA Measurement ID', file: '.env', status: 'Pendente config', notes: 'Definir REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 6: PERFORMANCE (CWV)
// ═══════════════════════════════════════════════════════════════════════════════
const PERFORMANCE_ITEMS = [
  { metric: 'LCP (Largest Contentful Paint)', target: '≤ 2.5s', file: 'src/utils/webVitals.js', status: 'Coletando', notes: 'Enviado ao GA4 como evento web_vitals' },
  { metric: 'CLS (Cumulative Layout Shift)', target: '≤ 0.1', file: 'src/utils/webVitals.js', status: 'Coletando', notes: 'Multiplicado por 1000 para envio como inteiro' },
  { metric: 'FID (First Input Delay)', target: '≤ 100ms', file: 'src/utils/webVitals.js', status: 'Coletando', notes: 'web-vitals 2.x usa FID (3.x usa INP)' },
  { metric: 'Performance Budget', target: 'JS ≤ 300KB gzip', file: 'CI/CD (futuro)', status: 'Planejar', notes: 'Validar bundle size em CI antes de deploy' },
  { metric: 'Font Preload', target: 'font-display: swap', file: 'public/index.html', status: 'Recomendado', notes: 'Precarregar fontes institucionais com <link rel="preload">' },
  { metric: 'Image aspect-ratio', target: 'CLS = 0', file: 'CSS/componentes', status: 'Recomendado', notes: 'Reservar espaço com aspect-ratio para evitar layout shift' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════
const StatusIcon = ({ status }) => {
  if (status === 'ok') return <CheckCircle2 className="w-4 h-4 text-green-600" />;
  if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-red-500" />;
};

const StatusBadge = ({ status, t }) => {
  const colors = { ok: 'bg-green-100 text-green-700', warning: 'bg-amber-100 text-amber-700', error: 'bg-red-100 text-red-700' };
  const labels = { ok: 'OK', warning: t?.badgeWarn || 'Atenção', error: t?.badgePending || 'Pendente' };
  return <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${colors[status]}`}>{labels[status]}</span>;
};

const TABS_ICONS = [Globe, CheckCircle2, FileText, Database, BarChart3, Zap];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const MarketingSeoDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { lang } = useGovernanceLang();
  const t = SEO_LABELS[lang] || SEO_LABELS.pt;

  const TABS = t.tabs.map((tab, i) => ({ ...tab, icon: TABS_ICONS[i] }));

  const okCount = SEO_STATUS.filter(s => s.status === 'ok').length;
  const warnCount = SEO_STATUS.filter(s => s.status === 'warning').length;
  const errorCount = SEO_STATUS.filter(s => s.status === 'error').length;
  const checkDone = CHECKLIST.filter(c => c.done).length;

  return (
    <div className="space-y-6">
      {/* ─── VUKZ Header ─── */}
      <div className="flex items-center gap-3">
        <Link to={R.marketingHub} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-500" />
        </Link>
        <img src="/vukz-logo.png" alt="VUKZ" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
        <div>
          <h1 className="text-gray-900 text-2xl font-extrabold">{t.pageTitle}</h1>
          <p className="text-gray-500 mt-0.5">{t.pageSubtitle}</p>
        </div>
      </div>

      {/* ─── Summary Cards ─── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{okCount}</div>
          <div className="text-xs text-gray-500 mt-1">{t.cardOk}</div>
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <div className="text-2xl font-bold text-amber-500">{warnCount}</div>
          <div className="text-xs text-gray-500 mt-1">{t.cardWarn}</div>
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <div className="text-2xl font-bold text-red-500">{errorCount}</div>
          <div className="text-xs text-gray-500 mt-1">{t.cardPending}</div>
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <div className="text-2xl font-bold" style={{ color: BRAND_GREEN }}>{checkDone}/{CHECKLIST.length}</div>
          <div className="text-xs text-gray-500 mt-1">{t.cardChecklist}</div>
        </div>
      </div>

      {/* ─── Tabs ─── */}
      <div className="border-b overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white border border-b-white -mb-px text-gray-900'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              style={activeTab === tab.id ? { borderBottomColor: 'white', color: BRAND_GREEN } : {}}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Tab Content ─── */}

      {/* TAB: Visão Geral */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-xl border divide-y">
          {SEO_STATUS.map((s) => (
            <div key={s.label} className="px-5 py-3 flex items-center gap-3">
              <StatusIcon status={s.status} />
              <div className="flex-1">
                <span className="font-medium text-sm text-gray-900">{s.label}</span>
                <p className="text-xs text-gray-500">{s.detail}</p>
              </div>
              <StatusBadge status={s.status} t={t} />
            </div>
          ))}
        </div>
      )}

      {/* TAB: Checklist */}
      {activeTab === 'checklist' && (
        <div className="bg-white rounded-xl border">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <Code className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>{t.checklistTitle}</h2>
            <span className="ml-auto text-xs text-gray-400">{t.checklistDone(checkDone, CHECKLIST.length)}</span>
          </div>
          <div className="divide-y">
            {CHECKLIST.map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                {c.done
                  ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                  : <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />
                }
                <span className={`text-sm ${c.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{c.item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: Páginas Estratégicas */}
      {activeTab === 'pages' && (
        <div className="bg-white rounded-xl border">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <FileText className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>{t.pagesTitle}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thPage}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thType}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thUrl}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thKeyword}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thSections}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thStatus}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {STRATEGIC_PAGES.map((p) => (
                  <tr key={p.url} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{p.page}</td>
                    <td className="px-5 py-3 text-gray-500">{p.type}</td>
                    <td className="px-5 py-3"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{p.url}</code></td>
                    <td className="px-5 py-3 text-gray-600 italic">{p.keyword}</td>
                    <td className="px-5 py-3 text-xs text-gray-500 max-w-xs">{p.sections}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${p.status === 'Ativo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: Structured Data */}
      {activeTab === 'structured' && (
        <div className="bg-white rounded-xl border">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <Database className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>{t.structuredTitle}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thSchema}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thLocation}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thFile}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thStatus}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thNotes}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {STRUCTURED_DATA_ITEMS.map((d) => (
                  <tr key={d.schema} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{d.schema}</td>
                    <td className="px-5 py-3 text-gray-500">{d.location}</td>
                    <td className="px-5 py-3"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{d.file}</code></td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${d.status === 'Ativo' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500 max-w-xs">{d.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: Analytics */}
      {activeTab === 'analytics' && (
        <div className="bg-white rounded-xl border">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <BarChart3 className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>{t.analyticsTitle}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thItem}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thFile}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thStatus}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thNotes}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ANALYTICS_ITEMS.map((a) => (
                  <tr key={a.item} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{a.item}</td>
                    <td className="px-5 py-3"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{a.file}</code></td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                        a.status === 'Ativo' ? 'bg-green-100 text-green-700' :
                        a.status === 'Pronto' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500 max-w-xs">{a.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-4 border-t bg-gray-50 rounded-b-xl">
            <p className="text-xs text-gray-500">
              {t.analyticsNote('REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX')}
            </p>
          </div>
        </div>
      )}

      {/* TAB: Performance */}
      {activeTab === 'performance' && (
        <div className="bg-white rounded-xl border">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <Zap className="w-5 h-5" style={{ color: BRAND_GREEN }} />
            <h2 className="text-lg font-bold" style={{ color: BRAND_GREEN }}>{t.performanceTitle}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thMetric}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thTarget}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thFile}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thStatus}</th>
                  <th className="px-5 py-3 font-semibold text-gray-600">{t.thNotes}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {PERFORMANCE_ITEMS.map((p) => (
                  <tr key={p.metric} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{p.metric}</td>
                    <td className="px-5 py-3"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{p.target}</code></td>
                    <td className="px-5 py-3"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{p.file}</code></td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                        p.status === 'Coletando' ? 'bg-green-100 text-green-700' :
                        p.status === 'Recomendado' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500 max-w-xs">{p.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-4 border-t bg-gray-50 rounded-b-xl space-y-2">
            <p className="text-xs text-gray-600 font-semibold">{t.perfFormulas}</p>
            <p className="text-xs text-gray-500">LCP = TTFB + Resource Load Delay + Resource Load Time + Element Render Delay</p>
            <p className="text-xs text-gray-500">CLS = Impact Fraction × Distance Fraction (deve ser &lt; 0.1)</p>
            <p className="text-xs text-gray-500">FID/INP ≤ 200ms para boa responsividade</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingSeoDashboardPage;
